import { useEffect, useState } from 'react';
import { usePublicClient } from 'wagmi';
import type { EstimateContractGasParameters } from 'viem';
import { useQuery } from '@tanstack/react-query';
import { CHAINS } from '@dapp/multi-lib/network';

export const MAX_CAPPED_TX_GAS_LIMIT = BigInt(16_700_000);

const GAS_LIMIT_CAPPED_CHAIN_IDS = new Set<number>([
  CHAINS.ETH_MAINNET,
  CHAINS.BASE_L2,
]);

type EstimateContractGasArgs = EstimateContractGasParameters & {
  enabled?: boolean;
  query?: { enabled?: boolean };
  chainId?: number;
};

export const getGasLimitCap = (chainId?: number) => {
  if (chainId === undefined) return undefined;

  return GAS_LIMIT_CAPPED_CHAIN_IDS.has(chainId)
    ? MAX_CAPPED_TX_GAS_LIMIT
    : undefined;
};

export const addBuffer = (gas: bigint, chainId?: number) => {
  const bufferedGas = gas + (gas / BigInt(100)) * BigInt(50);
  const gasLimitCap = getGasLimitCap(chainId);

  return gasLimitCap && bufferedGas > gasLimitCap ? gasLimitCap : bufferedGas;
};

export const useEstimateContractGas = (
  args: EstimateContractGasArgs,
  deps: unknown[]
) => {
  const publicClient = usePublicClient();

  const [gas, setGas] = useState<bigint | undefined>();

  useEffect(() => {
    const load = async () => {
      if (!publicClient) {
        throw new Error('No public client available');
      }

      try {
        const gas = await publicClient.estimateContractGas(args);
        setGas(addBuffer(gas, args.chainId ?? publicClient.chain?.id));
      } catch (e) {
        console.log('estimate gas error', e);
      }
    };

    if (args.query?.enabled || args.enabled) {
      load();
    }
  }, deps);

  return gas;
};

export const useQueryEstimateContractGas = (
  args: EstimateContractGasArgs & {
    query: { enabled?: boolean; queryKey: readonly unknown[] };
  }
) => {
  const publicClient = usePublicClient();

  return useQuery({
    queryKey: ['estimateContractGas', ...args.query.queryKey],
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('No public client available');
      }

      const gas = await publicClient.estimateContractGas(args);
      return addBuffer(gas, args.chainId ?? publicClient.chain?.id);

      return BigInt(0);
    },
    enabled: args.query.enabled,
  });
};
